import { Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NotesComponent } from '../notes/notes.component';
import { MemberDashboardService } from '../member-dashboard.service';
import { ActivatedRoute } from '@angular/router';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { CommonModule, formatDate } from '@angular/common';
import moment from 'moment';
import { NgIf } from '@angular/common';
import { MatDateRangeInput, MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule, MatHint, MatLabel } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-score-chart',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCheckboxModule, NotesComponent, MatNativeDateModule,
    MatDateRangeInput, MatHint, MatLabel, MatFormFieldModule, MatDatepickerModule,
    HighchartsChartModule, NgIf, MatProgressSpinnerModule],
  templateUrl: './score-chart.component.html',
  styleUrl: './score-chart.component.css'
})
export class ScoreChartComponent {

  @ViewChild('chart') chart: any;
  @Input() sDate: any;
  @Input() eDate: any;
  @Input() memberbasic: any;
  noDataFound: boolean = false;
  emptyMessage: string = '';
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  private chartInstance: any;
  showChart: boolean = true;
  isLoading: number = 1;
  tZone: any;
  currentDateScore: any;
  previousDateScore: any;
  memberId: any = this.route.snapshot.queryParamMap.get('memberId');
  updateFlag: boolean = false;
  oneToOneFlag: boolean = true;
  runOutsideAngular: boolean = false;
  endDate: any = formatDate(Date.now(), 'Y-MM-d', 'en');
  startDate: any = formatDate(new Date().setDate(new Date().getDate() - 7), 'Y-MM-d', 'en');
  scoreData: any = [];
  selectedVitals: { [key: string]: boolean } = {
    'hlth': false,  // Heald
    'slp': true,    // Sleep (default selected)
    'reco': false,  // Readiness
    'glu': false,   // Glucose
    'nut': false,   // Nutrition
    'acty': false,  // Activity
    'eng': false    // Engagement
  };

  constructor(private dashboard: MemberDashboardService, private route: ActivatedRoute) {
    var d = new Date();
    var p = new Date(new Date().setDate(new Date().getDate() - 7));
    this.currentDateScore = d
    this.previousDateScore = p;
  }

  ngOnInit() {
    if (this.memberbasic.timeZone && this.memberbasic.timeZone != '' && this.memberbasic.timeZone != "null") {
      this.tZone = this.memberbasic.timeZone
    } else {
      if (this.memberbasic.address?.country?.toUpperCase() === "INDIA") {
        this.tZone = 'Asia/Kolkata';
      } else {
        this.tZone = 'America/New_York';
      }
    }
    // Use input dates from parent if provided, otherwise use default dates
    const startDateToUse = this.sDate || this.startDate;
    const endDateToUse = this.eDate || this.endDate;
    this.getScoreDetails(startDateToUse, endDateToUse);
  }

  ngOnChanges(changes: SimpleChanges) {
    // React to changes in sDate or eDate inputs from parent
    if ((changes['sDate'] || changes['eDate']) && !changes['sDate']?.firstChange && !changes['eDate']?.firstChange) {
      const startDateToUse = this.sDate || this.startDate;
      const endDateToUse = this.eDate || this.endDate;
      this.isLoading = 1;
      this.getScoreDetails(startDateToUse, endDateToUse);
    }
  }
  filterByDate(startDate: HTMLInputElement, endDate: HTMLInputElement) {
    if (startDate.value != '' && endDate.value != '') {
      this.startDate = startDate.value;
      this.endDate = endDate.value;

      // Show loading spinner
      this.isLoading = 1;

      // Fetch new data - zoom will be reset after data loads
      this.getScoreDetails(startDate.value, endDate.value);
    }
  }

  setTimePeriod(period: string) {
    const today = new Date();
    let startDate: Date;

    switch (period) {
      case '1week':
        startDate = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));
        break;
      case '2weeks':
        startDate = new Date(today.getTime() - (14 * 24 * 60 * 60 * 1000));
        break;
      case '1month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        break;
      case '3months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        break;
      default:
        startDate = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));
    }

    // Update the date picker values
    this.previousDateScore = startDate;
    this.currentDateScore = today;

    // Format dates for API calls
    const startDateStr = formatDate(startDate, 'Y-MM-d', 'en');
    const endDateStr = formatDate(today, 'Y-MM-d', 'en');

    // Update internal date variables
    this.startDate = startDateStr;
    this.endDate = endDateStr;

    // Show loading spinner
    this.isLoading = 1;

    // Fetch score data for the selected period - zoom will be reset after data loads
    this.getScoreDetails(startDateStr, endDateStr);
  }
  onVitalToggle(vital: string) {
    // Show loading spinner to prevent layout flicker while updating
    this.isLoading = 1;
    // Force chart recreation when toggling vitals
    this.forceChartRecreation();
    // Wait for chart to be recreated, then create graph with new data
    setTimeout(() => {
      this.createMultiSeriesGraph();
    }, 150);
  }

  getScoreDetails(sDate: any, eDate: any) {
    this.dashboard.getScoreReadings(this.memberId, sDate, eDate).subscribe((data: any) => {
      const rawScoreData = data.result || {};
      const normalizedScoreData = {
        sleepScores: rawScoreData.sleepScores || [],
        nutritionScores: rawScoreData.nutritionScores || [],
        activityScores: rawScoreData.activityScores || [],
        glycemicScores: rawScoreData.glycemicScores || [],
        recoveryScores: rawScoreData.recoveryScores || [],
        healdScores: rawScoreData.healdScores || [],
        engagementScores: rawScoreData.engagementScores || []
      };

      this.scoreData = normalizedScoreData;
      this.noDataFound = normalizedScoreData.sleepScores.length == 0 && normalizedScoreData.nutritionScores.length == 0
        && normalizedScoreData.activityScores.length == 0 && normalizedScoreData.glycemicScores.length == 0
        && normalizedScoreData.recoveryScores.length == 0 && normalizedScoreData.healdScores.length == 0
        && normalizedScoreData.engagementScores.length == 0;

      if (this.noDataFound) {
        // If no data found, still render an empty chart with message overlay
        this.setEmptyChart();
        this.emptyMessage = 'No Record found for selected date range';
        this.isLoading = 2;
        return;
      }

      // Force chart recreation with new data
      this.forceChartRecreation();

      // Small delay to ensure chart is recreated before rendering new data
      setTimeout(() => {
        this.createMultiSeriesGraph();
      }, 150);
    });
  }

  private setEmptyChart(): void {
    // Provide a minimal chart so the empty-state message can overlay it
    this.chartOptions = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line'
      },
      title: { text: this.getChartTitle() },
      xAxis: [{
        type: 'datetime',
        gridLineWidth: 0,
        minorGridLineWidth: 0
      }],
      yAxis: [{
        title: { text: 'Score' },
        lineWidth: 1,           // show y-axis line
        gridLineWidth: 0,       // remove horizontal grid lines
        minorGridLineWidth: 0, // simple markings
      }],
      credits: { enabled: false },
      legend: { enabled: false },         // hide legend (no "Series 1")
      series: [{
        type: 'line',
        name: '',
        data: [],
        showInLegend: false,
        enableMouseTracking: true
      }]
    };
    // Ensure the view updates the chart instance
    this.updateFlag = true;
    this.showChart = true;
  }

  getSelectedVitals(): string[] {
    return Object.keys(this.selectedVitals).filter(key => this.selectedVitals[key]);
  }
  getScoreConfig(vitalType: string): any {
    // Standardized configuration for ALL score types
    const standardScoreConfig = {
      ranges: [
        { from: 0, to: 20, color: '#FF39390D', name: 'Poor' },
        { from: 20, to: 40, color: '#E580090D', name: 'Fair' },
        { from: 40, to: 60, color: '#FCD34D1A', name: 'Good' },
        { from: 60, to: 80, color: '#00D37D0D', name: 'Very Good' },
        { from: 80, to: 100, color: '#20AA4A0D', name: 'Excellent' }
      ],
      pointColors: {
        poor: '#FF3939',
        fair: '#E58009',
        good: '#FCD34D',
        veryGood: '#00D37D',
        excellent: '#20AA4A'
      }
    };

    // Use the same standardized config for all score types
    const configs: { [key: string]: any } = {
      'slp': standardScoreConfig,  // Sleep Score
      'nut': standardScoreConfig,  // Nutrition Score
      'acty': standardScoreConfig, // Activity Score
      'glu': standardScoreConfig,  // Glucose Score
      'hlth': standardScoreConfig, // Heald Score
      'reco': standardScoreConfig, // Recovery/Readiness Score
      'eng': standardScoreConfig   // Engagement Score
    };
    return configs[vitalType] || standardScoreConfig; // Default to standard config if not found
  }

  getScoreColor(score: number, vitalType: string): string {
    const config = this.getScoreConfig(vitalType);

    // Standardized logic for ALL score types
    if (score <= 20) return config.pointColors.poor;
    if (score <= 40) return config.pointColors.fair;
    if (score <= 60) return config.pointColors.good;
    if (score <= 80) return config.pointColors.veryGood;
    if (score <= 100) return config.pointColors.excellent;

    return config.pointColors.excellent; // Default to excellent for scores above 100
  }

  getVitalLineColor(vitalType: string): string {
    const lineColors: { [key: string]: string } = {
      'slp': '#4787F6',  // Blue for Sleep
      'nut': '#FF6B35',  // Orange for Nutrition
      'acty': '#2ECC71', // Green for Activity
      'glu': '#9B59B6',  // Purple for Glucose
      'reco': '#E74C3C', // Red for Readiness
      'hlth': '#F39C12', // Yellow/Orange for Heald
      'eng': '#FF4FA3'   // Blue for Engagement
    };
    return lineColors[vitalType] || '#000408';
  }

  getVitalName(vitalType: string): string {
    const names: { [key: string]: string } = {
      'slp': 'Sleep',
      'nut': 'Nutrition',
      'acty': 'Activity',
      'glu': 'Glucose',
      'reco': 'Readiness',
      'hlth': 'Heald',
      'eng': 'Engagement'
    };
    return names[vitalType] || vitalType;
  }

  getChartTitle(): string {
    const selectedVitals = this.getSelectedVitals();
    if (selectedVitals.length === 0) return 'Score Comparison';
    if (selectedVitals.length === 1) {
      return this.getVitalName(selectedVitals[0]) + ' Scores';
    }
    return 'Score Comparison - ' + selectedVitals.map(v => this.getVitalName(v)).join(', ');
  }

  createMultiSeriesGraph() {
    const selectedVitals = this.getSelectedVitals();

    if (selectedVitals.length === 0) {
      // Show empty chart with message when nothing selected
      this.noDataFound = true;
      this.setEmptyChart();
      this.emptyMessage = 'Please select at least one score to view data.';
      this.isLoading = 2;
      return;
    }

    // Reset noDataFound when at least one vital is selected
    this.noDataFound = false;

    const vitalMap: { [key: string]: { prop: string } } = {
      slp: { prop: 'sleepScores' },
      nut: { prop: 'nutritionScores' },
      acty: { prop: 'activityScores' },
      glu: { prop: 'glycemicScores' },
      reco: { prop: 'recoveryScores' },
      hlth: { prop: 'healdScores' },
      eng: { prop: 'engagementScores' }
    };

    // Prepare series data for each selected vital
    const seriesData: any[] = [];
    let allScores: number[] = [];

    selectedVitals.forEach((vital: string) => {
      const config = vitalMap[vital];
      if (!config) return;

      const vitalData = this.scoreData[config.prop];
      if (!vitalData || vitalData.length === 0) return;

      // Sort the input data by date ascending so the chart draws in chronological order.
      // Use a non-destructive copy to avoid mutating the original API response.
      const sortedVitalData = Array.isArray(vitalData)
        ? [...vitalData].sort((a: any, b: any) => {
          const ta = a && a.date ? new Date(a.date).getTime() : 0;
          const tb = b && b.date ? new Date(b.date).getTime() : 0;
          return ta - tb;
        })
        : [];

      const data = sortedVitalData.map((item: any) => {
        // Use the item's date directly; formatting/parsing keeps time at midnight
        // which is acceptable for daily scores. If timezones are needed, adapt to use moment/timezone.
        const date = item && item.date ? new Date(item.date).getTime() : NaN;
        const score = item.score;
        allScores.push(score);
        return {
          x: date,
          y: score
          // Removed color property so data points inherit the line color
        };
      });

      seriesData.push({
        name: this.getVitalName(vital),
        data: data,
        type: 'line',
        color: this.getVitalLineColor(vital),
        showInLegend: true,
        marker: {
          symbol: 'circle',
          enabled: true,
          radius: 3,
          lineWidth: 1,
          states: {
            hover: {
              enabled: true,
              radius: 5
            }
          }
        }
      });
    });

    // If no series have any points for the selected date range, show empty state
    if (seriesData.length === 0) {
      this.noDataFound = true;
      this.setEmptyChart();
      this.emptyMessage = 'No Record found for selected date range';
      this.isLoading = 2;
      return;
    }

    // Calculate min and max for y-axis
    const minScore = Math.min(...allScores);
    const maxScore = Math.max(...allScores);
    const padding = Math.max(5, (maxScore - minScore) * 0.1);
    const dataMin = Math.max(0, Math.floor((minScore - padding) / 10) * 10);
    let dataMax = Math.min(100, Math.ceil((maxScore + padding) / 10) * 10);

    // Get standardized config for ranges
    const config = this.getScoreConfig('slp'); // All vitals use same config now

    // Cap dataMax at the nearest configured upper range boundary
    const upperBounds: number[] = (config.ranges || []).map((r: any) => r.to).filter((b: number) => typeof b === 'number');
    if (upperBounds && upperBounds.length > 0) {
      const candidateCaps = upperBounds.filter((b) => b >= maxScore && b <= dataMax);
      if (candidateCaps.length > 0) {
        const nearestCap = Math.min(...candidateCaps);
        if (nearestCap < dataMax) {
          dataMax = nearestCap;
        }
      }
    }

    // Get relevant ranges for plot bands
    const relevantRanges = config.ranges.filter((range: any) =>
      (range.from <= dataMax && range.to >= dataMin)
    );

    // Calculate tick positions
    const rangeBoundaries: number[] = [];
    relevantRanges.forEach((range: any) => {
      if (range.from >= dataMin && range.from <= dataMax) rangeBoundaries.push(range.from);
      if (range.to >= dataMin && range.to <= dataMax) rangeBoundaries.push(range.to);
    });

    if (!rangeBoundaries.includes(dataMin)) rangeBoundaries.push(dataMin);
    if (!rangeBoundaries.includes(dataMax)) rangeBoundaries.push(dataMax);
    const tickPositions: number[] = Array.from(new Set(rangeBoundaries)).sort((a, b) => a - b);

    this.chartOptions = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x'
        },
        zooming: {
          type: 'x'
        }
      },
      title: { text: this.getChartTitle() },
      xAxis: [{
        type: 'datetime',
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S.%L',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%H:%M',
          day: '%a,<br/>%b %e',
          week: '%a,<br/>%b %e',
          month: '%b \'%y',
          year: '%Y'
        }
      }],
      credits: {
        enabled: false
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal'
      },
      yAxis: [{
        lineWidth: 1,
        min: dataMin,
        max: dataMax,
        title: {
          text: 'Score'
        },
        tickPositions: tickPositions,
        plotBands: config.ranges,
        plotLines: (() => {
          const dynamicLabels: any[] = [];

          relevantRanges.forEach((range: any) => {
            let labelPosition = range.from;

            if (range.from < dataMin) {
              labelPosition = dataMin;
            }

            if (labelPosition >= dataMin && labelPosition <= dataMax) {
              dynamicLabels.push({
                value: labelPosition,
                color: 'transparent',
                width: 0,
                label: {
                  text: range.name,
                  align: 'right',
                  verticalAlign: 'middle',
                  x: -10,
                  style: {
                    fontFamily: 'Urbanist',
                    fontWeight: 600,
                    fontStyle: 'Semi Bold',
                    fontSize: '14px',
                    color: 'black',
                    backgroundColor: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '2px solid #333',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    textShadow: 'none'
                  }
                }
              });
            }
          });

          return dynamicLabels;
        })()
      }],
      tooltip: {
        enabled: true,
        shared: true,
        formatter: function () {
          if (this.x !== undefined) {
            let conD = new Date(this.x).toLocaleString('en-US');
            let d = moment(conD).format('ddd, MMM D, YYYY');
            let tooltip = '<b>' + d + '</b><br/>';

            this.points?.forEach((point: any) => {
              tooltip += '<span style="color:' + point.color + '">\u25CF</span> ' +
                point.series.name + ': <b>' + point.y + '</b><br/>';
            });

            return tooltip;
          }
          return '';
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '12pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: true
        },
        series: {
          enableMouseTracking: true,
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 1,
          }
        },
      },
      series: seriesData
    };

    // Hide loading spinner after chart is created
    this.isLoading = 2;

    // Add chart callback to capture instance for zoom control
    this.chartOptions.chart!.events = {
      load: this.chartCallback
    };
  }

  private chartCallback = (chart: any) => {
    // The chart parameter is actually an event object, get the actual chart from target
    const actualChart = chart.target;
    this.chartInstance = actualChart;
  }


  private resetChartZoom(): void {
    const chart = this.getChartInstance();
    if (chart && chart.xAxis && chart.xAxis[0] && typeof chart.xAxis[0].setExtremes === 'function') {
      chart.xAxis[0].setExtremes(null, null);
    }
  }

  // Force chart recreation when data changes
  private forceChartRecreation(): void {
    // Hide and show chart to force recreation
    this.showChart = false;
    setTimeout(() => {
      this.showChart = true;
    }, 150);
  }

  // Get chart instance from ViewChild if not available
  private getChartInstance(): any {
    if (this.chartInstance) {
      return this.chartInstance;
    }

    // Try to get from ViewChild
    if (this.chart && this.chart.chart) {
      this.chartInstance = this.chart.chart;
      return this.chartInstance;
    }

    return null;
  }
}
